import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {CopyButton} from 'src/components/copy-button';
import {ErrorFlatButton} from 'src/components/error-flat-button';
import {DateInput, FormInput} from 'src/components/input';
import {AccountLink} from 'src/components/links/account-link';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl, ValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {getAccountHierarchyTree} from 'src/openapi/admin/apis/AccountHierarchyApi';
import {SsoConnectionRole} from 'src/openapi/admin/models/SsoConnection';
import {UserResetPeriod as UserResetPeriods} from 'src/openapi/admin/models/UserResetPeriod';
import {useAppDispatch} from 'src/store/hooks';

import Button, {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import FormSection from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {usePushNotification} from '@pb/kit-component-push-notification';
import {Textarea} from '@pb/kit-component-textarea-v2';
import Typography from '@pb/kit-component-typography';

import {SsoSettingsInheritance} from './connection-inheritance';
import {
    DOMAINS_CONTROL,
    DOMAINS_REQUIRED_ERROR_CODE,
    DOMAINS_REQUIRED_ERROR_MESSAGE,
    DOMAIN_URL_FORBIDDEN_SYMBOL,
    SECTION_NAME,
    SSO_CERTIFICATE_CONTROL,
    SSO_CERTIFICATE_REQUIRED_ERROR_CODE,
    SSO_CERTIFICATE_REQUIRED_ERROR_MESSAGE,
    SSO_URL_CONTROL,
    SSO_URL_REQUIRED_ERROR_CODE,
    SSO_URL_REQUIRED_ERROR_MESSAGE,
} from './constants';
import {SsoSettingsDeletion} from './delete-connection';
import {useResetDateSettings, useSsoName, useSsoSettings} from './hooks';
import {useSsoParentAccount} from './hooks/useSsoParentAccount';
import {ImportMetadata} from './import-metadata';
import {SsoTabSkeleton} from './sso-tab.skeleton';
import {
    closeDomainForbiddenNote,
    sanitizeDomainValue as sanitizeDomain,
    showDomainForbiddenNote as triggerDomainForbiddenNote,
} from './utils';

import type {UserResetPeriod} from 'src/openapi/admin/models/UserResetPeriod';


type OnCheckBoxChange = (data: {event: React.SyntheticEvent<HTMLInputElement, Event>, name: string}) => void;

type Props = {
    accountId?: number,
    title?: string,
    showInheritSettings?: boolean,
    headerStickToElement?: string,
};

const NewSsoTab = ({
    accountId: providedAccountId,
    title = SECTION_NAME,
    showInheritSettings = true,
    headerStickToElement,
}: Props) => {
    const queryParams = useParams<{accountId?: string}>();
    const accountId = providedAccountId ?? (queryParams.accountId ? +queryParams.accountId : -1);

    const [saveBusy, setSaveBusy] = useState(false);
    const [inheritBusy, setInheritBusy] = useState(false);
    const [inheritError, setInheritError] = useState<string | null>(null);
    const apiDispatch = useAppDispatch();

    const {canEditSso} = useAdminAccessRight();
    const {removeError, clearErrors, addError} = useContext(ValidationContext);
    const {accountSubmit, accountFieldChange} = useEventTracking();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {onCreateNote, onCloseNote} = usePushNotification();
    const domainWarningNoteIdRef = useRef<string | null>(null);

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'account-sso-tab',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isSsoNameLoading,
        loadingError: ssoNameLoadingError,
        data: ssoName,
        loadData: loadSsoName,
    } = useSsoName({accountId});

    const {
        isLoading: isSsoLoading,
        loadingError: ssoLoadingError,
        isChanged: isSsoSettingsChanged,
        data: ssoSettings,
        setData: setSsoSettings,
        saveChanges: saveSsoSettings,
        discardChanges: discardSsoSettings,
        loadData: loadSsoSettings,
    } = useSsoSettings({accountId});

    const {
        isLoading: isResetDateSettingsLoading,
        loadingError: resetDateSettingsLoadingError,
        isChanged: isResetDateSettingsChanged,
        data: resetDateSettings,
        setData: setResetDateSettings,
        saveChanges: saveResetDateSettings,
        discardChanges: discardResetDateSettings,
        loadData: loadResetDateSettings,
    } = useResetDateSettings({accountId});

    const {
        data: parentAccount,
        isLoading: isParentAccountLoading,
        loadingError: parentAccountLoadingError,
        loadData: loadParentAccount,
    } = useSsoParentAccount({accountId});

    const canEdit = canEditSso(+accountId);
    const isInherited = ssoSettings.role === SsoConnectionRole.CHILD;
    const canEditFields = canEdit && !inheritBusy && !saveBusy;
    const canEditConfiguration = canEditFields && !isInherited;

    useEffect(() => {
        setIsChunkChanged(isSsoSettingsChanged || isResetDateSettingsChanged);
    }, [isSsoSettingsChanged, isResetDateSettingsChanged, setIsChunkChanged]);

    const validateSsoSettings = useCallback(() => {
        let valid = true;
        if (!ssoSettings.ssoUrl) {
            addError({
                controlId: SSO_URL_CONTROL,
                code: SSO_URL_REQUIRED_ERROR_CODE,
                message: SSO_URL_REQUIRED_ERROR_MESSAGE,
            });
            valid = false;
        }

        if (!ssoSettings.ssoCertificate) {
            addError({
                controlId: SSO_CERTIFICATE_CONTROL,
                code: SSO_CERTIFICATE_REQUIRED_ERROR_CODE,
                message: SSO_CERTIFICATE_REQUIRED_ERROR_MESSAGE,
            });
            valid = false;
        }

        if (!ssoName.ssoConnectionName) {
            const domains = [...ssoSettings.domains];
            domains.forEach((domain, i) => {
                if (!domain) {
                    addError({
                        controlId: `${DOMAINS_CONTROL}-${i}`,
                        code: DOMAINS_REQUIRED_ERROR_CODE,
                        message: DOMAINS_REQUIRED_ERROR_MESSAGE,
                    });
                    valid = false;
                }
            });
        }

        return valid;
    }, [ssoSettings.ssoUrl, ssoSettings.ssoCertificate, ssoSettings.domains, ssoName.ssoConnectionName, addError]);

    const refreshData = useCallback(async () => {
        clearErrors();
        await Promise.all([loadSsoSettings(), loadResetDateSettings(), loadSsoName(), loadParentAccount()]);
    }, [loadSsoSettings, loadResetDateSettings, loadSsoName, loadParentAccount, clearErrors]);

    const refreshParentAccount = useCallback(async () => {
        await Promise.all([loadParentAccount(), apiDispatch(getAccountHierarchyTree({accountId}))]);
    }, [loadParentAccount, apiDispatch, accountId]);

    const saveChanges = useCallback(async () => {
        accountSubmit({submit: true, place: SECTION_NAME});
        const valid = validateSsoSettings();

        if (!valid) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            return;
        }

        const calls = [];
        let saveError;

        setSaveBusy(true);
        if (isSsoSettingsChanged) {
            saveError = await saveSsoSettings();
            calls.push(saveError);
        }

        if (isResetDateSettingsChanged && !saveError) {
            calls.push(await saveResetDateSettings());
        }
        setSaveBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];

        if (errors.length) {
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
            refreshData();
        }
    }, [
        isSsoSettingsChanged,
        saveSsoSettings,

        isResetDateSettingsChanged,
        saveResetDateSettings,
        accountSubmit,
        setErrorMessage,
        proceed,
        refreshData,
        validateSsoSettings,
    ]);

    const discardChanges = useCallback(() => {
        discardSsoSettings();
        discardResetDateSettings();
        clearErrors();

        accountSubmit({submit: false, place: SECTION_NAME});
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardSsoSettings, discardResetDateSettings, clearErrors, accountSubmit, setSuccessMessage]);

    const trackParameterChange = useCallback((parameterName: string) => {
        accountFieldChange({
            section: SECTION_NAME,
            viewType: 'Existed',
            parameterName,
        });
    }, [accountFieldChange]);

    const onSsoMandatoryChange = useCallback<OnCheckBoxChange>(e => {
        trackParameterChange('SSO Mandatory');
        setSsoSettings(state => ({
            ...state,
            ssoMandatory: e.event.currentTarget.checked,
        }));
    }, [setSsoSettings, trackParameterChange]);

    const onSsoEnabledChange = useCallback<OnCheckBoxChange>(e => {
        trackParameterChange('Enable SSO');
        const checked = e.event.currentTarget.checked;
        setSsoSettings(state => ({
            ...state,
            ssoEnabled: checked,
            ...(!checked && {ssoMandatory: false}),
        }));
    }, [setSsoSettings, trackParameterChange]);

    const onSsoJitEnabledChange = useCallback<OnCheckBoxChange>(e => {
        trackParameterChange('Enable User Provisioning');
        setSsoSettings(state => ({
            ...state,
            jitEnabled: e.event.currentTarget.checked,
        }));
    }, [setSsoSettings, trackParameterChange]);

    const changeSsoUrl = useCallback((signOnServiceLocation: string) => {
        trackParameterChange('Identity Provider (IdP) Login URL');
        setSsoSettings(state => ({
            ...state,
            ssoUrl: signOnServiceLocation,
        }));

        if (signOnServiceLocation) {
            removeError(SSO_URL_CONTROL);
        }
    }, [removeError, setSsoSettings, trackParameterChange]);

    const changeCertificate = useCallback((certificate: string) => {
        const ssoCertificate = certificate.trim();
        trackParameterChange('Trusted Signing Certificate');
        setSsoSettings(state => ({...state, ssoCertificate}));
        if (ssoCertificate) {
            removeError(SSO_CERTIFICATE_CONTROL);
        }
    }, [removeError, trackParameterChange, setSsoSettings]);

    const handleCertificateChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSsoSettings(state => ({...state, ssoCertificate: event.target.value}));
    }, [setSsoSettings]);

    const blurCertificate = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setSsoSettings(state => ({...state, ssoCertificate: event.target.value.trim()}));
    }, [setSsoSettings]);

    const showDomainForbiddenNote = useCallback(() => {
        triggerDomainForbiddenNote({
            noteIdRef: domainWarningNoteIdRef,
            onCreateNote,
            onCloseNote,
        });
    }, [onCreateNote, onCloseNote]);

    const sanitizeDomainValue = useCallback((domain: string) => {
        if (!domain || !domain.includes(DOMAIN_URL_FORBIDDEN_SYMBOL)) {
            return domain;
        }

        showDomainForbiddenNote();

        return sanitizeDomain(domain);
    }, [showDomainForbiddenNote]);

    const validateDomainValue = useCallback((domain: string) => {
        if (!domain) {
            return true;
        }

        if (domain.includes(DOMAIN_URL_FORBIDDEN_SYMBOL)) {
            showDomainForbiddenNote();
            return false;
        }

        return true;
    }, [showDomainForbiddenNote]);

    const changeDomain = useCallback((domain: string, index: number) => {
        const sanitizedDomain = sanitizeDomainValue(domain);
        trackParameterChange('Add Account Domain');
        const domains = [...ssoSettings.domains];
        domains[index] = sanitizedDomain;
        setSsoSettings(state => ({
            ...state,
            domains,
        }));
        if (sanitizedDomain) {
            removeError(`${DOMAINS_CONTROL}-${index}`);
        }
    }, [sanitizeDomainValue, ssoSettings.domains, removeError, setSsoSettings, trackParameterChange]);

    const removeDomain = useCallback((index: number) => {
        trackParameterChange('Remove Account Domain');
        setSsoSettings(state => ({
            ...state,
            domains: state.domains.filter((_, i) => i !== index),
        }));
        removeError(`${DOMAINS_CONTROL}-${index}`);
    }, [removeError, setSsoSettings, trackParameterChange]);

    const addDomain = useCallback(() => {
        trackParameterChange('Add Account Domain');
        setSsoSettings(state => ({
            ...state,
            domains: [...state.domains, ''],
        }));
    }, [trackParameterChange, setSsoSettings]);

    const selectUserResetPeriod = useCallback((resetPeriod?: UserResetPeriod) => {
        trackParameterChange('User Reset Period');
        setResetDateSettings(state => ({...state, resetPeriod}));
    }, [trackParameterChange, setResetDateSettings]);

    const setResetDateOptional = useCallback<OnCheckBoxChange>(e => {
        trackParameterChange('No Reset Date Required');
        setResetDateSettings(state => ({
            ...state,
            resetOptional: e.event.currentTarget.checked,
        }));
    }, [trackParameterChange, setResetDateSettings]);

    const changeNextResetDateChange = useCallback((nextResetDate: string | null) => {
        trackParameterChange('Next User Reset Date');
        setResetDateSettings(state => ({
            ...state,
            nextResetDate: nextResetDate ? nextResetDate : undefined,
        }));
    }, [setResetDateSettings, trackParameterChange]);

    const isLoading = useMemo(
        () => isSsoNameLoading || isSsoLoading || isResetDateSettingsLoading || isParentAccountLoading,
        [isSsoNameLoading, isSsoLoading, isResetDateSettingsLoading, isParentAccountLoading],
    );

    const serverError = useMemo(
        () => ssoNameLoadingError || ssoLoadingError || resetDateSettingsLoadingError || parentAccountLoadingError,
        [ssoNameLoadingError, ssoLoadingError, resetDateSettingsLoadingError, parentAccountLoadingError],
    );

    const isDeletionAllowed = useMemo(
        () => !ssoSettings.ssoEnabled || !canEdit || !ssoName.ssoConnectionName,
        [ssoSettings.ssoEnabled, ssoName.ssoConnectionName, canEdit],
    );

    useEffect(() => {
        return () => {
            closeDomainForbiddenNote({
                noteIdRef: domainWarningNoteIdRef,
                onCloseNote,
            });
        };
    }, [onCloseNote]);

    return (
        <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
            <Skeleton shown={isLoading ? !inheritBusy : false} view={<SsoTabSkeleton/>}>
                <ServerResponseHandler serverError={serverError} sectionName={SECTION_NAME}>
                    <ProfileMain
                        stickToElement={headerStickToElement}
                        header={(
                            <Item grow={1}>
                                <Group justify="space-between">
                                    <Typography fontWeight="semi-bold" variant="heading-level-3">{title}</Typography>
                                    {canEdit ? (
                                        <Group>
                                            <Button
                                                variant="Type.SECONDARY"
                                                disabled={!isCurrentChunkChanged || saveBusy || inheritBusy}
                                                onClick={discardChanges}>
                                                Discard Changes
                                            </Button>
                                            <Button
                                                busy={saveBusy}
                                                variant="Type.PRIMARY"
                                                disabled={!isCurrentChunkChanged || saveBusy || inheritBusy}
                                                onClick={saveChanges}>
                                                Save Changes
                                            </Button>
                                        </Group>
                                    ) : null}
                                </Group>
                            </Item>
                        )}>
                        <FormSection headline="Configure SAML 2.0 Authentication">
                            <Group direction="v">
                                <WidthItem width={650}>
                                    <Group alignItems="start">
                                        <Item grow={1}>
                                            <Group direction="v" gap="s">
                                                <CheckBox
                                                    label="Enable SSO"
                                                    disabled={!canEditConfiguration}
                                                    checked={ssoSettings.ssoEnabled}
                                                    onCheckBoxChange={onSsoEnabledChange}
                                                />
                                                <Item className="group-item pdx-l">
                                                    <CheckBox
                                                        label="SSO Mandatory"
                                                        disabled={(!canEdit && !saveBusy) || !ssoSettings.ssoEnabled}
                                                        checked={ssoSettings.ssoMandatory}
                                                        onCheckBoxChange={onSsoMandatoryChange}
                                                    />
                                                </Item>
                                            </Group>
                                        </Item>
                                        <ImportMetadata
                                            disabled={!ssoSettings.ssoEnabled || !canEditConfiguration}
                                            changeSsoUrl={changeSsoUrl}
                                            changeCertificate={changeCertificate}
                                            canImportMetadata={!ssoName.ssoConnectionName}
                                        />
                                        {showInheritSettings ? (
                                            <SsoSettingsInheritance
                                                disabled={!canEdit || saveBusy || isLoading}
                                                accountId={accountId}
                                                parentAccount={parentAccount}
                                                hasUnsavedChanges={isSsoSettingsChanged || isResetDateSettingsChanged}
                                                onInherit={refreshData}
                                                onParentSaved={refreshParentAccount}
                                                onBusyChange={setInheritBusy}
                                                onErrorChange={setInheritError}
                                            />
                                        ) : null}
                                        <SsoSettingsDeletion
                                            disabled={isDeletionAllowed || inheritBusy || saveBusy}
                                            accountId={accountId}
                                            onDeleted={refreshData}
                                        />
                                    </Group>
                                </WidthItem>
                                {showInheritSettings && inheritError ? (
                                    <Message color="error" style={{maxWidth: '650px'}}>
                                        {inheritError}
                                    </Message>
                                ) : null}
                                {isInherited && parentAccount && !isLoading ? (
                                    <Message color="info" style={{maxWidth: '650px'}}>
                                        SSO settings are inherited from parent account{' '}
                                        <AccountLink {...parentAccount} blank={true} place={SECTION_NAME}/>
                                        .
                                    </Message>
                                ) : null}
                                <Group>
                                    {!!ssoName.ssoConnectionName && (
                                        <WidthItem width={650}>
                                            <Label>SSO Name</Label>
                                            <Group>
                                                {ssoName.ssoConnectionName}
                                                <CopyButton
                                                    entityName="SSO Name"
                                                    copiedText={ssoName.ssoConnectionName}
                                                />
                                            </Group>
                                        </WidthItem>
                                    )}
                                </Group>
                                <CustomValidatedControl name={SSO_URL_CONTROL}>
                                    {error => (
                                        <FormInput
                                            width={650}
                                            label="Identity Provider (IdP) Login URL"
                                            placeholder="Enter your account's HTTPS Endpoint"
                                            readOnly={!canEditConfiguration}
                                            disabled={!ssoSettings.ssoEnabled || !canEditConfiguration}
                                            value={ssoSettings.ssoUrl}
                                            onBlur={changeSsoUrl}
                                            valid={error ? false : undefined}
                                            errorDescription={error}
                                        />
                                    )}
                                </CustomValidatedControl>
                                <ValidatedControl name={SSO_CERTIFICATE_CONTROL}>
                                    {invalid => (
                                        <WidthItem width={650}>
                                            <Label>Trusted Signing Certificate</Label>
                                            <Textarea
                                                style={{height: '26.33rem'}}
                                                value={ssoSettings.ssoCertificate || ''}
                                                placeholder="Copy and paste your x.509 certificate here"
                                                disabled={!canEditConfiguration || !ssoSettings.ssoEnabled}
                                                onChange={handleCertificateChange}
                                                onBlur={blurCertificate}
                                                color={invalid ? 'ERROR' : 'DEFAULT'}
                                            />
                                        </WidthItem>
                                    )}
                                </ValidatedControl>
                                <WidthItem width={650}>
                                    <Label>Account Domains</Label>
                                    <Group direction="v" gap="xs">
                                        {[
                                            ...ssoSettings.domains.length
                                                ? ssoSettings.domains
                                                : [''],
                                        ].map((domain, index) => (
                                            <Group key={index} alignItems="start">
                                                <CustomValidatedControl name={`${DOMAINS_CONTROL}-${index}`}>
                                                    {error => (
                                                        <FormInput
                                                            width={300}
                                                            placeholder="Domain URL"
                                                            value={domain}
                                                            disabled={!ssoSettings.ssoEnabled || !canEditFields}
                                                            readOnly={!canEditFields}
                                                            validation={validateDomainValue}
                                                            onBlur={value => changeDomain(value, index)}
                                                            valid={error ? false : undefined}
                                                            errorDescription={error}
                                                        />
                                                    )}
                                                </CustomValidatedControl>
                                                {ssoSettings.domains.length > 1 &&
                                                    canEditFields && ssoSettings.ssoEnabled ? (
                                                            <Item className="group-item pdy-s">
                                                                <ErrorFlatButton
                                                                    icon={Icons.TIMES_CIRCLE}
                                                                    label="Remove"
                                                                    onClick={() => removeDomain(index)}
                                                                />
                                                            </Item>
                                                        ) : null}
                                            </Group>
                                        ))}
                                        <WidthItem width={100}>
                                            <FlatButton
                                                disabled={!canEditFields || !ssoSettings.ssoEnabled}
                                                onClick={addDomain}
                                                startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}>
                                                <b>Add Domain</b>
                                            </FlatButton>
                                        </WidthItem>
                                    </Group>
                                </WidthItem>
                            </Group>
                        </FormSection>
                        <FormSection headline="Optional Configuration">
                            <Group direction="v">
                                <CheckBox
                                    label="Enable User Provisioning"
                                    disabled={!canEditConfiguration || !ssoSettings.ssoEnabled}
                                    checked={ssoSettings.jitEnabled}
                                    onCheckBoxChange={onSsoJitEnabledChange}
                                />
                                <CheckBox
                                    label="No Reset Date Required"
                                    disabled={
                                        !canEditConfiguration || !ssoSettings.ssoEnabled || !ssoSettings.jitEnabled
                                    }
                                    checked={resetDateSettings.resetOptional}
                                    onCheckBoxChange={setResetDateOptional}
                                />
                                <WidthItem width={300}>
                                    <Label>Next User Reset Date</Label>
                                    <DateInput
                                        id="next-reset-date-picker"
                                        readonly={!canEditConfiguration}
                                        clearable={true}
                                        disabled={
                                            !canEditConfiguration ||
                                            !ssoSettings.ssoEnabled ||
                                            resetDateSettings.resetOptional ||
                                            !ssoSettings.jitEnabled
                                        }
                                        selectedDate={resetDateSettings?.nextResetDate || ''}
                                        onChange={changeNextResetDateChange}
                                    />
                                </WidthItem>
                                <WidthItem width={300}>
                                    <Label>User Reset Period</Label>
                                    <ComboBox
                                        onChange={selectUserResetPeriod}
                                        disabled={
                                            !canEditConfiguration ||
                                            !ssoSettings.ssoEnabled ||
                                            resetDateSettings.resetOptional ||
                                            !ssoSettings.jitEnabled
                                        }
                                        value={resetDateSettings.resetPeriod}>
                                        {Object.values(UserResetPeriods).map(item => {
                                            return (
                                                <ComboboxItem key={item} caption={item + ' months'} value={+item}/>
                                            );
                                        })}
                                    </ComboBox>
                                </WidthItem>
                                <WidthItem width={300}>
                                    <Label>Last User Reset Date</Label>
                                    {resetDateSettings?.lastResetDate || 'N/A'}
                                </WidthItem>
                            </Group>
                        </FormSection>
                    </ProfileMain>
                </ServerResponseHandler>
            </Skeleton>
        </ErrorBoundary>
    );
};

export const ValidatedSSOTab = (props: Props) => {
    const {accountId} = useParams<{accountId?: string}>();
    return (
        <ValidationContextProvider key={props.accountId ?? accountId}>
            <NewSsoTab {...props}/>
        </ValidationContextProvider>
    );
};
